

<template>
  <div
    class="search-bar-vue-component"
    :class="className"
    :data-expanded="expanded || undefined"
    ref="searchBar"
  >
    <div class="header">
      <Button
        ref="toggleMobile"
        :aria-expanded="false"
        :left-icon="expanded ? 'ArrowLeft_24' : 'Search_24'"
        size="small"
        :aria-label="labels.searchToggleBtn"
        class-name="toggleWrapMobile"
        type="button"
        variant="Tertiary"
        no-background
        @click="toggleExpand"
      />

      <Button
        v-if="!expanded"
        ref="toggleDesktop"
        :aria-expanded="false"
        left-icon="Search_24"
        size="small"
        class-name="toggleWrapDesktop"
        inner-class-name="toggleSolidButton"
        :text="labels.searchToggleBtn"
        type="button"
        variant="Tertiary"
        @click="toggleExpand"
      />

      <div
        v-if="expanded"
        class="expandSurface"
        :class="{ expandSurfaceWide: surfaceWide }"
      >
        <form
          ref="form"
          class="form"
          :class="{ typedTransitions }"
          :data-has-value="hasValue || undefined"
          role="search"
          @submit.prevent="handleSubmit"
        >
          <span class="leadingWrap">
            <span class="leadingIcon" aria-hidden="true"
              ><Icon name="Search_24"
            /></span>
          </span>

          <div class="field">
            <input
              ref="input"
              class="input"
              autocomplete="off"
              autocorrect="off"
              role="combobox"
              aria-autocomplete="list"
              :aria-expanded="expanded && resolvedSuggestions ? 'true' : 'false'"
              :aria-controls="`${effectiveId}-suggestions`"
              :id="`${effectiveId}-input`"
              :aria-label="labels.inputLabel"
              :placeholder="labels.inputPlaceholder"
              type="search"
              :value="currentValue"
              required
              @input="setValue($event.target.value)"
              @keydown="handleInputKeyDown"
            />
          </div>

          <div class="trailing" :inert="!hasValue">
            <button
              type="button"
              class="trailingBtnWrap trailingBtn trailingBtnClear"
              :aria-label="labels.clearBtn"
              @click="handleClear" 
            >
              <Icon name="Close_16" />
            </button>

            <span aria-hidden="true" class="divider"></span>

            <button
              type="submit"
              class="trailingBtnWrap trailingBtn"
              :aria-label="labels.submitBtn"
            >
              <Icon name="Search_24" />
            </button>
          </div>
        </form>
      </div>  
    </div>

    <SearchBarSuggestions 
      v-if="expanded && resolvedSuggestions"
      ref="suggestions"
      :class="mergedSuggestionsClass"
      :id="`${effectiveId}-suggestions`"
      :intercept-bottom-link="interceptBottomLink"
      :query="currentValue"
      :suggestions="resolvedSuggestions"
      @bottom-link-click="handleSuggestionsBottomLinkClick"
      @escape="closeSearch({ restoreFocus: true })"
      @focus-out="handleSuggestionsFocusOut"
      @item-click="handleSuggestionItemClick"
      @show-all-click="handleSuggestionsShowAllClick"
    />
  </div>
</template>

<script>
import SearchBarSuggestions from "@/components/SearchBarSuggestions.vue";
import Button from "@/components/Button.vue";
import Icon from "@/components/Icon.vue";
import { getRecentSearchKeywords, recordRecentSearchKeyword, } from "@/libraries/recentSearchKeywords";
import { endSearchSession, ensureSearchInitiated, trackSearchAbandonedOnClose, trackSearchNoResults, trackSearchShowAll, trackSearchSuggestionClick } from "@/libraries/searchTracking";

const FETCH_DEBOUNCE_MS = 300;

export default {
    name: "SearchBar",

    components: {
        SearchBarSuggestions,
        Button,
        Icon,
    },

       
    props: {
        brand: {
            type: String,
            default: 'giant',
        },
        locale: {
            type: String,
            default: '',
        },
        className: {
            type: String,
            default: "",
        },
        defaultValue: {
            type: String,
            default: "",
        },
        id: {
            type: String,
            default: "",
        },
        modelValue: {
            type: String,
            default: undefined,
        },
        suggestions: {
            type: Array,
            default: null,
        },
        suggestionsClassName: {
            type: String,
            default: "",
        },
    },

    emits: [
        "update:modelValue",
        "change",
        "clear",
        "expanded",
        "submit",
        "suggestions-bottom-link-click",
    ],

    data() {
        const initialHasQuery =
            this.defaultValue.trim().length > 0 ||
            (this.modelValue !== undefined &&
            String(this.modelValue).trim().length > 0);

        return {
            searchBarDomSuffix: randomDomIdSuffix(),
            internalValue: this.defaultValue,
            open: this.defaultValue.trim().length > 0,
            surfaceWide: initialHasQuery,
            typedTransitions: !initialHasQuery,
            skipWidthEnterAnimation: initialHasQuery,
            isTabletOrAbove: false,

            fetchedSuggestions: null,
            suggestionsIdle: true,
            interceptBottomLink: false,
            debounceTimer: null,
            fetchController: null,
            lastNoResultsSearchTerm: null,

            labels: {
                searchToggleBtn: "Search",
                inputLabel: "Search",
                inputPlaceholder: "What are you looking for?",
                clearBtn: "Clear search",
                submitBtn: "Submit search",
                recentSearchesTitle: "Recent searches",
            },

        };

        function randomDomIdSuffix() {
            if (typeof crypto !== "undefined" && crypto.randomUUID) {
                return crypto.randomUUID();
            }
            return Math.random().toString(36).slice(2, 11);
        }
    },

    computed: {
        generatedId() {
            return `search-bar-${this.searchBarDomSuffix}`;
        },

        effectiveId() {
            return this.id || this.generatedId;
        },

        currentValue() {
            return this.modelValue !== undefined
            ? this.modelValue
            : this.internalValue;
        },

        hasValue() {
            return String(this.currentValue).trim().length > 0;
        },

        expanded() {
            return this.open || this.hasValue;
        },

        resolvedSuggestions() {
            let base = null;
            if (Array.isArray(this.suggestions) && this.suggestions.length > 0) {
                base = this.suggestions;
            } else if (
                Array.isArray(this.fetchedSuggestions) &&
                this.fetchedSuggestions.length > 0
            ) {
                base = this.fetchedSuggestions;
            }
            const recent = this.buildRecentSearchSection();
            const showRecent = !this.hasValue && recent;
            if (showRecent && base) {
                return [...base, recent];
            }
            if (showRecent && !base) {
                return [recent];
            }
            return base;
        },
        mergedSuggestionsClass() {
            return [
                this.suggestionsClassName,
                { "search-bar-suggestions--idle": this.suggestionsIdle },
            ];
        },
    },

    watch: {
        open(value) {
            this.lockBodyScroll(value);
            this.syncDesktopOutsideCloseListener();
        },

        expanded(value) {
            requestAnimationFrame(() => {
            this.surfaceWide = Boolean(value);
            });
        },
    },

    mounted() {
        this.updateBreakpoint();
        window.addEventListener("resize", this.updateBreakpoint);

        if (this.skipWidthEnterAnimation) {
            requestAnimationFrame(() => {
                this.typedTransitions = true;
            });
        }
    },

    beforeUnmount() {
        window.removeEventListener("resize", this.updateBreakpoint);
        document.removeEventListener(
            "pointerdown",
            this.handleDocumentPointerDown,
            true,
        );
        this.lockBodyScroll(false);
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
        this.fetchController?.abort();
        this.fetchController = null;
    },

    methods: {
        normalizeSuggestions(payload) {
            if (!payload) return null;

            if (Array.isArray(payload)) {
                return this.normalizeSectionsArray(payload);
            }

            const sections = payload.sections ?? payload.Sections;
            if (Array.isArray(sections)) {
                return this.normalizeSectionsArray(sections);
            }

            const suggestions = payload.suggestions ?? payload.Suggestions;
            if (Array.isArray(suggestions)) {
                return this.normalizeSectionsArray([
                    {
                        title: payload.title ?? payload.Title ?? "",
                        items: suggestions,
                    }, 
                ]);
            }

            const results = payload.results ?? payload.Results;
            if (Array.isArray(results)) {
                return this.normalizeSectionsArray(results);
            }

            return null;
        },

        normalizeSectionsArray(sections) {
            const normalized = sections
                .map((section) => this.normalizeSection(section))
                .filter((section) => section && section.items.length > 0);

            return normalized.length ? normalized : null;
        },

        normalizeSection(section) {
            if (!section) return null;

            const rawItems = section.items ?? section.Items ?? [];
            const items = rawItems
                .map((item) => this.normalizeItem(item))
                .filter((item) => item && item.title);

            const bottomLink = this.normalizeBottomLink(
                section.bottomLink ?? section.BottomLink,
            );

            return {
                title: section.title ?? section.Title ?? "",
                items,
                bottomLink,
            };
        },

        normalizeItem(item) {
            if (!item) return null;

            return {
                title: item.title ?? item.Title ?? item.name ?? item.Name ?? "",
                url: item.url ?? item.Url ?? "",
                image: item.image ?? item.Image,
                subtitle: item.subtitle ?? item.Subtitle,
                titleHighlighted: item.titleHighlighted ?? item.TitleHighlighted,
                subtitleHighlighted: item.subtitleHighlighted ?? item.SubtitleHighlighted,
                icon: item.icon ?? item.Icon,
                brand: item.brand ?? item.Brand,
                type: item.type ?? item.Type,
                category: item.category ?? item.Category,
                contentType: item.contentType ?? item.ContentType,
            };
        },

        normalizeBottomLink(link) {
            if (!link) return undefined;

            const normalized = this.normalizeItem(link);
            if (!normalized?.title) return undefined;

            return {
                title: normalized.title,
                url: normalized.url,
            };
        },

        async fetchSearchSuggestions() {
            //console.log("about to fetch suggestions");
            //console.log("this.effectiveLocale: " + this.effectiveLocale);

            if (!this.effectiveLocale) return;

            this.fetchController?.abort();
            const controller = new AbortController();
            this.fetchController = controller;

            const q = String(this.currentValue || '').trim();

            try {
                const culture = window.culture || 'global';
                const culturePrefix = culture.startsWith('/') ? culture : `/${culture}`;
                const base = `${culturePrefix}/apiproxy/search`;

                const url = new URL(q ? `${base}/instant` : `${base}/suggestions`, window.location.origin);

                url.searchParams.set(
                    'Brand',
                    String(this.effectiveBrand || 'giant').toLowerCase(),
                );
                url.searchParams.set('Locale', this.effectiveLocale);

                if (q) {
                    url.searchParams.set('query', q);
                }

                const res = await fetch(url.toString(), {
                    credentials: 'same-origin',
                    headers: { Accept: 'application/json' },
                    signal: controller.signal,
                });

                if (!res.ok) throw new Error(String(res.status));

                const data = await res.json();

                if (this.fetchController !== controller) return;

                this.fetchedSuggestions = this.normalizeSuggestions(data);
                this.suggestionsIdle = !q;

                if (q) {
                    if (this.fetchedSuggestions) {
                        this.lastNoResultsSearchTerm = null;
                    } else if (this.lastNoResultsSearchTerm !== q) {
                        this.lastNoResultsSearchTerm = q;
                        trackSearchNoResults(q);
                    }
                }
            } catch (e) {
                if (e.name === 'AbortError') return;
                if (this.fetchController === controller) {
                    this.fetchedSuggestions = null;
                }
            } finally {
                if (this.fetchController === controller) {
                    this.fetchController = null;
                }
            }
        },

        scheduleFetchSuggestions() {
            if (!this.effectiveLocale) return;

            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => {
                this.debounceTimer = null;

                if (!this.expanded) return;

                this.fetchSearchSuggestions();
            }, FETCH_DEBOUNCE_MS);
        },

        setValue(next) {
            if (this.modelValue === undefined) {
                this.internalValue = next;
            }

            const trimmed = String(next || "").trim();
            if (trimmed) {
                ensureSearchInitiated(trimmed);
            }

            this.$emit("update:modelValue", next);
            this.$emit("change", next);
            this.scheduleFetchSuggestions();
        },

        toggleExpand() {
            if (!this.open) {
                this.$emit("expanded");
                this.fetchSearchSuggestions();
                this.open = true;
                this.$nextTick(() => {
                    this.$refs.input?.focus({ preventScroll: true });
                });
            } else {
                this.closeSearch();
            }
        },

        handleClear() {
          this.setValue("");
          this.$emit("clear");
          this.$refs.input?.focus();
        },

        handleSuggestionItemClick({ item, position }) {
            const destinationType = item?.type || "";
            const isProduct = destinationType === "pdp";

            trackSearchSuggestionClick({
                category: destinationType === "plp" ? item?.title : undefined,
                contentType: item?.contentType,
                destinationType,
                destinationUrl: item?.url || "",
                position,
                productName: isProduct ? item?.title : undefined,
                productSubcategory: isProduct ? item?.category : undefined,
                searchTerm: String(this.currentValue || "").trim(),
            });
        },

        handleSubmit() {
            // next js:
            // this.$emit('submit', keyword);

            const keyword = String(this.currentValue || '').trim();
            if (!keyword) return;

            recordRecentSearchKeyword(keyword);
            endSearchSession();

            const culture = typeof window !== 'undefined' && window.culture
                ? window.culture
                : 'global';
            const prefix = culture.startsWith('/') ? culture : `/${culture}`;
            const url = `${prefix}/search?keyword=${encodeURIComponent(keyword)}`;

            window.location.assign(url);
        },

        getMainSiteSearchUrlForKeyword(keyword) {
            const k = String(keyword || "").trim();
            if (!k) return "";
            const culture =
                typeof window !== "undefined" && window.culture
                    ? window.culture
                    : "global";
            const prefix = culture.startsWith("/") ? culture : `/${culture}`;
            return `${prefix}/search?keyword=${encodeURIComponent(k)}`;
        },

        buildRecentSearchSection() {
            const keys = getRecentSearchKeywords();
            if (!keys.length) return null;
            return {
                title: this.labels.recentSearchesTitle,
                items: keys.map((title) => ({
                    title,
                    url: this.getMainSiteSearchUrlForKeyword(title),
                    type: "search",
                })),
            };
        },

        // Fires on a plain click of a "view all results" link, also when the browser
        // handles the navigation itself and bottom-link-click never runs.
        handleSuggestionsShowAllClick({ destinationUrl, searchTerm }) {
            trackSearchShowAll({ destinationUrl, searchTerm });
            endSearchSession();
        },

        handleSuggestionsBottomLinkClick(keyword) {
            this.$emit("suggestions-bottom-link-click", keyword);

            const url = this.getMainSiteSearchUrlForKeyword(
                keyword || this.currentValue,
            );
            if (url) {
                window.location.assign(url);
            }
        },

        closeSearch(options = {}) {
              trackSearchAbandonedOnClose(
                String(this.currentValue || "").trim() || undefined,
              );
              this.setValue("");
              this.open = false;

              if (options.restoreFocus) {
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    const toggle = this.isTabletOrAbove
                      ? this.$refs.toggleDesktop
                      : this.$refs.toggleMobile;

                    toggle?.focus();
                  });
                });
              }
        },

        handleInputKeyDown(event) {
            if (event.key === "Escape") {
                event.preventDefault();
                this.closeSearch({ restoreFocus: true });
                return;
            }

            if (event.key === "ArrowDown") {
                event.preventDefault();
                this.$refs.suggestions?.focusFirstAnchor?.();
            }
        },

        handleDocumentPointerDown(event) {
            if (!this.open && !this.hasValue) {
                return;
            }

            if (this.$refs.searchBar?.contains(event.target)) {
                return;
            }

            this.closeSearch();
        }, 
         
        syncDesktopOutsideCloseListener() {
            document.removeEventListener(
                "pointerdown",
                this.handleDocumentPointerDown,
                true,
            );

            if (this.open && this.isTabletOrAbove) {
                document.addEventListener(
                    "pointerdown",
                    this.handleDocumentPointerDown,
                    true,
                );
            }
        },

        handleSuggestionsFocusOut() {
          this.$refs.input?.focus();
        },

        updateBreakpoint() {
            const wasTabletOrAbove = this.isTabletOrAbove;
            this.isTabletOrAbove = window.matchMedia("(min-width: 768px)").matches;

            if (wasTabletOrAbove !== this.isTabletOrAbove) {
                this.syncDesktopOutsideCloseListener();
            }
        },

        lockBodyScroll(locked) {
            document.body.style.overflow = locked ? "hidden" : "";
        },
    },
    created() {
        const el = document.getElementById("vue-search-bar");

        this.effectiveLocale = el?.dataset.locale || this.locale || "";
        this.effectiveBrand = el?.dataset.brand || this.brand || "giant";
        this.interceptBottomLink = el?.dataset.interceptBottomLink === "true";
    },
};
</script>
